<template>
    <div class="container mx-auto px-3 pb-32 md:px-8">
        <page-header class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/automation/flows" class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Flows
                </a>
            </template>

            {{ flow.name }}
        </page-header>

        <div class="grid grid-cols-12">
            <flow-sidebar :flowId="props.flowId"></flow-sidebar>

            <div v-if="loading" class="col-span-12 lg:col-span-10 text-center py-10">
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>
            <div v-else class="col-span-12 lg:col-span-10 space-y-8">
                <div class="flex flex-col-reverse gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <flow-resources-filters
                        v-model="filters"
                        @reset="resetFilters"
                        :loading="loading"
                    ></flow-resources-filters>
                    <vx-button @click="addNewSection = true" type="button" size="lg" color="primary">
                        New Section
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>

                <page-callout class="mt-8" section="form-submission" shadow />

                <div ref="sectionsListRef" class="space-y-8 w-full">
                    <flow-section
                        v-for="(element, index) in sections"
                        :key="element.draggable_id"
                        v-model:open="element.open"
                        :flowId="flow.flow_id"
                        :flowSection="index"
                        :section="element"
                        @draggable:change="changeDraggable"
                        @remove="removeSection(index)"
                    ></flow-section>
                </div>
            </div>
        </div>

        <flow-section-form v-model:visible="addNewSection"></flow-section-form>
    </div>
</template>

<script setup>
import { faArrowLeft, faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { useToasted } from '~/composables/useToasted';
import FlowResourcesFilters from './FlowResourcesFilters.vue';
import FlowSection from './FlowSection.vue';
import FlowSidebar from './FlowSidebar.vue';
import { useFlow } from './useFlow';
import { useUrlSearchParams } from '@vueuse/core';
import { useSortable, moveArrayElement } from '@vueuse/integrations/useSortable';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import debounce from '~/utils/debounce';
import PageCallout from '~/components/general/PageCallout.vue';
import FlowSectionForm from '~/components/automation/flows/flow/FlowSectionForm.vue';

const toasted = useToasted();
const { mount, flow, sections, removeSection, saveSections } = useFlow();

const props = defineProps({
    flowId: {
        type: String,
    },
});

const defaultFilters = () => ({
    query: '',
});

const filters = ref({});
const addNewSection = ref(false);

const sectionsListRef = ref(null);

useSortable(sectionsListRef, sections, {
    handle: '.section-handle',
    animation: 200,
    group: { name: 'sections' },
    watchElement: true,
    onUpdate: (evt) => {
        moveArrayElement(sections, evt.oldIndex, evt.newIndex, evt);
        nextTick(() => changeDraggable());
    },
});

const resetFilters = () => {
    filters.value = defaultFilters();
};

const loading = ref(false);

const queryParams = useUrlSearchParams();

const changeDraggable = debounce(async () => {
    try {
        await saveSections(sections.value);
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
        } else {
            toasted.error(e.response?.data?.message || 'Something went wrong.');
        }
    }
}, 500);

onMounted(async () => {
    loading.value = true;
    try {
        await mount(props.flowId);
        if (queryParams.flow_section != null) {
            sections.value[queryParams.flow_section].open = true;
        }
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
});
</script>
